import React, { useEffect, useState, useMemo } from "react";
import {
    Container, Card, CardBody, CardTitle, Button,
    Table, Modal, ModalHeader, ModalBody, Spinner, Row, Col, FormGroup, Input, Label
} from "reactstrap";
import Session from "../auth/Session";
import SurchargeForm from "./SurchargeForm";
import SearchableDropdownFilter from "./SearchableDropdownFilter";
import useSortableData from "./useSortableData";
import SortableHeader from "./SortableHeader";

const getDriverLabel = (driver) =>
    `${driver.driverName}${driver.businessName ? ` — ${driver.businessName}` : ""}`;

const isDriverSurcharge = (surcharge) =>
    (surcharge.targetType ?? (surcharge.driverId ? "Driver" : (surcharge.routeId ? "Route" : "SubCustomer"))) === "Driver";
const isVisibleDriverSurcharge = (surcharge, includeInactive) =>
    isDriverSurcharge(surcharge) && (includeInactive || surcharge.isActive !== false);

const DriverSurchargeGrid = () => {
    const apiBaseUrl = "/api/surcharges";
    const driversApi = "/api/drivers/list";

    const [drivers, setDrivers] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState("");
    const [includeInactive, setIncludeInactive] = useState(false);
    const [surcharges, setSurcharges] = useState([]);
    const [loading, setLoading] = useState(false);

    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const token = await Session.getToken();
                const res = await fetch(driversApi, { headers: { Authorization: `Bearer ${token}` } });
                setDrivers(res.ok ? await res.json() : []);
            } catch (e) {
                console.error("Failed to load drivers", e);
            }
        })();
    }, []);

    const loadSurcharges = async () => {
        setLoading(true);
        try {
            const token = await Session.getToken();
            const url = selectedDriverId ? `${apiBaseUrl}/ByDriver/${selectedDriverId}` : apiBaseUrl;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const data = res.ok ? await res.json() : [];
            setSurcharges((data || []).filter(s => isVisibleDriverSurcharge(s, includeInactive)));
        } catch (e) {
            console.error("Failed to load driver surcharges", e);
            setSurcharges([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSurcharges();
    }, [selectedDriverId, includeInactive]);

    const handleSaved = () => {
        setOpen(false);
        loadSurcharges();
    };

    const getDriverName = (surcharge) => {
        const assignments = surcharge.driverAssignments || [];
        if (assignments.length > 0) {
            return assignments
                .map(a => a.driver ? getDriverLabel(a.driver) : a.driverId)
                .join(", ");
        }
        if (surcharge.driverIds && surcharge.driverIds.length > 0) {
            return surcharge.driverIds
                .map(id => {
                    const driver = drivers.find(d => `${d.id}` === `${id}`);
                    return driver ? getDriverLabel(driver) : id;
                })
                .join(", ");
        }
        if (surcharge.driver) return getDriverLabel(surcharge.driver);
        if (surcharge.driverId) {
            const driver = drivers.find(d => `${d.id}` === `${surcharge.driverId}`);
            return driver ? getDriverLabel(driver) : surcharge.driverId;
        }
        return <span className="text-muted">Available for manual selection</span>;
    };

    // String version of the driver cell so sorting matches what is displayed
    // (the JSX fallback becomes "" so unassigned surcharges sort to the bottom).
    const getDriverSortName = (surcharge) => {
        const assignments = surcharge.driverAssignments || [];
        if (assignments.length > 0) {
            return assignments.map(a => a.driver ? getDriverLabel(a.driver) : a.driverId).join(", ");
        }
        if (surcharge.driverIds && surcharge.driverIds.length > 0) {
            return surcharge.driverIds.map(id => {
                const driver = drivers.find(d => `${d.id}` === `${id}`);
                return driver ? getDriverLabel(driver) : id;
            }).join(", ");
        }
        if (surcharge.driver) return getDriverLabel(surcharge.driver);
        if (surcharge.driverId) {
            const driver = drivers.find(d => `${d.id}` === `${surcharge.driverId}`);
            return driver ? getDriverLabel(driver) : surcharge.driverId;
        }
        return "";
    };

    const sortGetters = useMemo(() => ({
        driver: getDriverSortName,
        transactionType: (s) => s.transactionType?.transactionName ?? s.transactionTypeId,
        surchargeType: (s) => s.surchargeType?.name ?? "Dollar Amount",
        amount: (s) => Number(s.surchargeType?.name === "Percentage" ? s.percentage : s.dollarAmount),
        status: (s) => (s.isActive === false ? "Inactive" : "Active"),
        // getDriverSortName closes over `drivers`, so resort when the roster loads.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [drivers]);

    const { items: sortedSurcharges, sortConfig, requestSort } = useSortableData(
        surcharges,
        { key: "driver", direction: "asc", getters: sortGetters }
    );

    return (
        <Container className="mt-5">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle tag="h5" className="mb-0">Driver Surcharge Management</CardTitle>
                        <Button color="primary" onClick={() => { setEditing(null); setOpen(true); }}>
                            + New Driver Surcharge
                        </Button>
                    </div>

                    <Row className="mb-3">
                        <Col md={6}>
                            <FormGroup>
                                <SearchableDropdownFilter
                                    value={selectedDriverId}
                                    options={drivers}
                                    placeholder="Filter by Driver..."
                                    getOptionLabel={getDriverLabel}
                                    onChange={setSelectedDriverId}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6} className="d-flex align-items-center">
                            <FormGroup check className="mb-0">
                                <Label check>
                                    <Input
                                        id="includeInactiveDriverSurcharges"
                                        type="checkbox"
                                        checked={includeInactive}
                                        onChange={(e) => setIncludeInactive(e.target.checked)}
                                    />{" "}
                                    Show inactive driver surcharges
                                </Label>
                            </FormGroup>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="py-5 text-center">
                            <Spinner />
                        </div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <SortableHeader label="Driver" sortKey="driver" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Surcharge" sortKey="description" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Transaction Type" sortKey="transactionType" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Calculation Type" sortKey="surchargeType" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Amount" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSurcharges.length === 0 ? (
                                    <tr><td colSpan="7" className="text-center">No driver surcharges found</td></tr>
                                ) : sortedSurcharges.map(s => (
                                    <tr key={s.id} className={s.isActive === false ? 'text-muted fst-italic' : ''}>
                                        <td>{getDriverName(s)}</td>
                                        <td>{s.description}</td>
                                        <td>{s.transactionType?.transactionName ?? s.transactionTypeId}</td>
                                        <td>{s.surchargeType?.name ?? "Dollar Amount"}</td>
                                        <td style={{ textAlign: "right" }}>{s.surchargeType?.name === "Percentage" ? `${Number(s.percentage).toFixed(2)}%` : `$${Number(s.dollarAmount).toFixed(2)}`}</td>
                                        <td>{s.isActive === false ? <span className="text-muted">Inactive</span> : "Active"}</td>
                                        <td className="text-end">
                                            <Button size="sm" color="info" onClick={() => { setEditing(s); setOpen(true); }}>
                                                Edit
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={open} toggle={() => setOpen(false)} size="lg">
                <ModalHeader toggle={() => setOpen(false)}>
                    {editing ? "Edit Driver Surcharge" : "New Driver Surcharge"}
                </ModalHeader>
                <ModalBody>
                    <SurchargeForm
                        surchargeItem={
                            editing ?? {
                                id: 0,
                                driverId: selectedDriverId,
                                targetType: "Driver",
                            }
                        }
                        apiBaseUrl={apiBaseUrl}
                        allowedTargetTypes={["Driver"]}
                        defaultTargetType="Driver"
                        onSuccess={handleSaved}
                        onCancel={() => setOpen(false)}
                    />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default DriverSurchargeGrid;
